import { Page } from "model/page";
import { isSettingUp, isStatusPage, normalizeSlug, pathToSave, slugError } from "../page-catalog";

/** What the Set Up / Edit modal lets the user change. */
export interface ModalValues {
	/**
	 * The whole path, query variables included (`/listing?id={id}`), as the old SEO Pages editor
	 * edited it (MAX-4577). It opens as stored; once edited it always carries a leading slash, which
	 * the input itself doesn't show.
	 */
	slug: string;
	/** The custom-slug toggle; the page's `enabled` once saved. */
	enabled: boolean;
	title: string;
	head: string;
}

/** The values the modal opens with: the page as saved, the toggle included (MAX-4574). */
export function initialValues(page: Page): ModalValues {
	return {
		slug: page.path || "",
		enabled: page.enabled,
		title: page.title || "",
		head: page.head || "",
	};
}

function slugChanged(page: Page, initial: ModalValues, values: ModalValues): boolean {
	return (isStatusPage(page) || values.enabled) && normalizeSlug(values.slug) !== normalizeSlug(initial.slug);
}

/**
 * Whether "Reset to default" shows: while the slug editor is open and the path differs from the
 * type's default. That includes a saved path that fails validation, so there is always a way out.
 */
export function resetVisible(page: Page, values: ModalValues, editing: boolean): boolean {
	return editing && canResetToDefault(page, values);
}

/**
 * The warning that a live page's URL has changed, so the website must change to match (OSEO 13). It
 * appears only once the user commits the change (Done, or Reset to default), not as they type, and
 * stays until the modal closes: `committed` is the modal's flag for that, cleared whenever the modal
 * opens. Returning the slug to what is saved, or an invalid slug (which cannot be saved), takes it
 * away. A page being set up has no live URL to break.
 */
export function urlWarningVisible(page: Page, initial: ModalValues, values: ModalValues, committed: boolean): boolean {
	return committed && !isSettingUp(page) && slugChanged(page, initial, values) && !slugProblem(page, values);
}

/** The slug's validation message, or undefined: a hidden slug field (toggle off) isn't validated. */
export function slugProblem(page: Page, values: ModalValues): string | undefined {
	const statusPage = isStatusPage(page);
	const shown = statusPage || values.enabled;
	// The Default IDX Page is the base of every disabled page's link (`/idx?path=...`), so it can't carry a query.
	// Judged as the input shows it, without its leading slashes: a stored "//search" isn't something the user
	// can see to fix, and it would otherwise block every save on the page.
	return shown ? slugError(values.slug.replace(/^\/+/, "/"), statusPage) : undefined;
}

/**
 * Whether the user has changed anything worth saving or discarding, compared with what the modal
 * opened with.
 *
 * - The path is compared normalised, so a leading or trailing slash alone is not a change.
 * - The path only counts while its field is shown (the toggle is on, or the page is the Default
 *   IDX Page, which is never switched off); a hidden field's edit is dropped on save.
 */
export function isDirty(page: Page, initial: ModalValues, current: ModalValues): boolean {
	const slugShown = isStatusPage(page) || current.enabled;
	const pathChanged = slugShown && normalizeSlug(current.slug) !== normalizeSlug(initial.slug);
	const toggled = current.enabled !== initial.enabled;
	return pathChanged || toggled || current.title !== initial.title || current.head !== initial.head;
}

/**
 * The primary button. Setting up a page, the button is never held back for "nothing changed": with
 * the toggle on, enabling is the change; with it off, Save has to be able to close the modal. Editing
 * a page, it waits for a change, as before.
 */
export function primaryButton(
	page: Page,
	values: ModalValues,
	options: { dirty: boolean; saving: boolean; invalid: boolean },
): { label: string; disabled: boolean } {
	const settingUp = isSettingUp(page);
	const label = options.saving ? "Saving..." : settingUp && values.enabled ? "Complete Setup" : "Save Changes";
	const disabled = options.saving || options.invalid || (!settingUp && !options.dirty);
	return { label, disabled };
}

/**
 * The path to write: the stored one, byte for byte, unless the user edited it, and then exactly what
 * they typed (MAX-4577). A hidden field's edit is dropped.
 */
function savedPath(page: Page, values: ModalValues, slugShown: boolean): string {
	if (!slugShown || values.slug === page.path) {
		return page.path;
	}
	return pathToSave(values.slug);
}

/**
 * The page to PUT, or undefined to close without saving.
 *
 * - Setting up with the toggle on enables the page at the path the user chose.
 * - Setting up with the toggle off never enables anything: the page stays Disabled. It saves only
 *   a changed title or head, and with nothing changed it just closes.
 * - Editing saves as it always has: the path only while the toggle is on, otherwise the saved path.
 */
export function pageToSave(page: Page, values: ModalValues, dirty: boolean): Page | undefined {
	if (isSettingUp(page) && !values.enabled) {
		return dirty ? { ...page, path: page.path, enabled: false, title: values.title, head: values.head } : undefined;
	}
	const slugShown = isStatusPage(page) || values.enabled;
	return {
		...page,
		path: savedPath(page, values, slugShown),
		enabled: values.enabled,
		title: values.title,
		head: values.head,
	};
}

/** Reset to default shows while the path differs from the type's default, query variables included. */
export function canResetToDefault(page: Page, values: ModalValues): boolean {
	const defaultPath = page.type.defaultPath;
	return !!defaultPath && values.slug !== defaultPath;
}
